import React from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid, FormControl, InputLabel, Select, MenuItem, TextField, FormControlLabel, Checkbox } from "@material-ui/core";
import { ClubSelect, DateSelect } from "components";
import { getBookings } from "api/admin/bookings";
import { Membership } from "api/memberships";
import i18n from "./i18n";

// prop types
type Filters = Parameters<typeof getBookings>[0];
interface FilterDialogProps {
    clubId?: string,
    filters: Filters,
    memberships: Membership[],
    show: boolean,
    onClose: () => void,
    onRefreshBookingsTable: () => void,
    onSetClubId: (clubId?: string) => void,
    onSetFilters: (filters: Filters) => void,
}

// booking types
const bookingTypes = ["standard", "meetup", "training", "blocking"] as const;

// booking status
const bookingStatus = ["final", "pending", "cancelled"] as const;

// filter bookings dialog
const FilterDialog = ({ clubId, filters, memberships, show, onClose, onRefreshBookingsTable, onSetClubId, onSetFilters }: FilterDialogProps) => {
    // apply action
    const applyAction = async () => {
        onRefreshBookingsTable(); // fetch update data
        onClose();
    };

    // show sip filter
    let showSIPFilter = false;
    const permittedMemberships = memberships.filter((m) => ["admin", "owner", "trainer"].includes(m.userRole));
    if (clubId && permittedMemberships.find((m) => m.club.id === clubId)?.club.capabilities.includes("sip")) showSIPFilter = true; // if current club is sip
    else if (!clubId && permittedMemberships.find((m) => m.club.capabilities.includes("sip"))) showSIPFilter = true; // or user is member of a sip club

    // render
    return (
        <Dialog
            fullWidth
            open={show}
            onClose={onClose}
        >
            <DialogTitle>{i18n.filter}</DialogTitle>
            <DialogContent dividers>
                <Grid container spacing={2}>

                    {/* booking type */}
                    <Grid item xs={6}>
                        <FormControl fullWidth>
                            <InputLabel>{i18n.bookingType}</InputLabel>
                            <Select
                                label={i18n.bookingType}
                                value={filters.bookingType || ""}
                                onChange={(e) => onSetFilters({ ...filters, bookingType: e.target.value as Filters["bookingType"] || undefined })}
                            >
                                <MenuItem value=""><em>{`(${i18n.all})`}</em></MenuItem>
                                {bookingTypes.map((t) => (
                                    <MenuItem key={t} value={t}>{i18n.bookingTypes[t]}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* booking status */}
                    <Grid item xs={6}>
                        <FormControl fullWidth>
                            <InputLabel>{i18n.status}</InputLabel>
                            <Select
                                label={i18n.status}
                                value={filters.bookingStatus || ""}
                                onChange={(e) => onSetFilters({ ...filters, bookingStatus: e.target.value as Filters["bookingStatus"] || undefined })}
                            >
                                <MenuItem value=""><em>{`(${i18n.all})`}</em></MenuItem>
                                {bookingStatus.map((t) => (
                                    <MenuItem key={t} value={t}>{i18n.bookingStatus[t]}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* club */}
                    <Grid item xs={12}>
                        <ClubSelect
                            clubId={clubId}
                            controlProps={{ fullWidth: true }}
                            userRoles={["owner", "admin", "trainer"]}
                            onChange={onSetClubId}
                        />
                    </Grid>

                    {/* bookings from */}
                    <Grid item xs={6}>
                        <DateSelect
                            label={i18n.bookingsFrom}
                            value={filters.fromDate}
                            onChange={(m) => onSetFilters({ ...filters, fromDate: m?.startOf("day").toISOString() })}
                        />
                    </Grid>

                    {/* bookings to */}
                    <Grid item xs={6}>
                        <DateSelect
                            label={i18n.bookingsTo}
                            value={filters.toDate}
                            onChange={(m) => onSetFilters({ ...filters, toDate: m?.endOf("day").toISOString() })}
                        />
                    </Grid>

                    {/* booking query */}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label={i18n.bookingQuery}
                            value={filters.bookingQuery || ""}
                            onChange={(e) => onSetFilters({ ...filters, bookingQuery: e.target.value })}
                        />
                    </Grid>

                    {/* participant query */}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label={i18n.participantQuery}
                            value={filters.participantQuery || ""}
                            onChange={(e) => onSetFilters({ ...filters, participantQuery: e.target.value })}
                        />
                    </Grid>

                    {/* sip form */}
                    {showSIPFilter && (
                        <Grid item xs={12}>
                            <FormControl>
                                <FormControlLabel
                                    control={(
                                        <Checkbox
                                            checked={(filters.attachedObjects || []).includes("sip-feedback-form")}
                                            color="primary"
                                            onChange={(e) => onSetFilters({ ...filters, attachedObjects: (e.target.checked ? ["sip-feedback-form"] : []) })}
                                        />
                                    )}
                                    label={i18n.sipForm}
                                />
                            </FormControl>
                        </Grid>
                    )}
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button color="secondary" onClick={() => { onSetFilters({}); applyAction(); }} style={{ marginRight: "auto" }}>
                    {i18n.reset}
                </Button>
                <Button color="secondary" onClick={onClose}>
                    {i18n.cancel}
                </Button>
                <Button color="primary" onClick={applyAction}>
                    {i18n.apply}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default FilterDialog;
